import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { AccountType } from "../types";
import { useLicenseCheck } from "../utils/license";
import logger from "../utils/logger";

// Single shared license state for the whole app. useLicenseCheck sets up a Firestore
// listener and (on cache miss) calls the rate-limited generateUserKey Cloud Function,
// so it must run exactly once — components consume it via useLicense() instead of
// calling useLicenseCheck() themselves.
type LicenseContextType = ReturnType<typeof useLicenseCheck> & {
  /** null while the license status is still loading, then mirrors hasValidLicense */
  hasActiveLicense: boolean | null;
  accountType: AccountType | null;
  breweryId: string | null;
  isBreweryAdmin: boolean;
  isBreweryMember: boolean;
  isHomebrewer: boolean;
  accountTypeLoading: boolean;
};

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);

export const LicenseProvider = ({ children }: { children: ReactNode }) => {
  const license = useLicenseCheck();
  const { user } = useAuth();

  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [breweryId, setBreweryId] = useState<string | null>(null);
  const [accountTypeLoading, setAccountTypeLoading] = useState(true);

  // Listen to user document for accountType and breweryId
  useEffect(() => {
    if (!user?.uid) {
      setAccountType(null);
      setBreweryId(null);
      setAccountTypeLoading(false);
      return;
    }

    const userDocRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setAccountType(data.accountType || null);
          setBreweryId(data.breweryId || null);
        } else {
          setAccountType(null);
          setBreweryId(null);
        }
        setAccountTypeLoading(false);
      },
      (error) => {
        logger.error("Error listening to user doc for accountType:", error);
        setAccountTypeLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Treat null/undefined accountType as homebrewer
  const effectiveType = accountType || "homebrewer";
  const isBreweryAdmin = effectiveType === "brewery_admin";
  const isBreweryMember = effectiveType === "brewery_member";
  const isHomebrewer = effectiveType === "homebrewer";

  const value: LicenseContextType = {
    ...license,
    hasActiveLicense: license.loading ? null : license.hasValidLicense,
    accountType,
    breweryId,
    isBreweryAdmin,
    isBreweryMember,
    isHomebrewer,
    accountTypeLoading,
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
};

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useLicense must be used within a LicenseProvider");
  }
  return context;
};
